import React from 'react';
import { Box, styled } from '@mui/material';
import { useDomainTheme } from '../../contexts/DomainThemeContext';

// Import logo assets
import decoyLogoVrp from '../../assets/logos/decoy-logo-vrp.png';
import decoyLogoFleet from '../../assets/logos/decoy-logo-fleet.png';
import decoyLogoEnterprise from '../../assets/logos/decoy-logo-enterprise.png';

export interface DecoyLogoProps {
  /** Size variant for different contexts */
  size?: 'small' | 'medium' | 'large' | 'header';
  /** Whether to show domain text below logo */
  showDomainText?: boolean;
  /** Custom height override */
  height?: number;
  /** Click handler for domain switching */
  onClick?: () => void;
  /** Force specific domain (for testing) */
  forceDomain?: 'vrp' | 'fleet' | 'enterprise';
}

const LogoContainer = styled(Box)<{
  clickable: boolean;
  size: string;
}>(({ theme, clickable, size }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: clickable ? 'pointer' : 'default',
  transition: 'transform 0.2s ease-in-out',
  
  // Size-specific styles
  ...(size === 'small' && {
    '& img': {
      height: '32px',
      width: 'auto',
    },
  }),
  ...(size === 'medium' && {
    '& img': {
      height: '48px',
      width: 'auto',
    },
  }),
  ...(size === 'large' && {
    '& img': {
      height: '64px',
      width: 'auto',
    },
  }),
  ...(size === 'header' && {
    '& img': {
      height: '40px',
      width: 'auto',
    },
  }),
  
  // Hover effects for clickable logos
  ...(clickable && {
    '&:hover': {
      transform: 'scale(1.05)',
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
  }),
}));

const DomainText = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

/**
 * DecoyLogo Component
 * 
 * Professional DECOY logo with domain-specific variants
 * Features:
 * - Responsive sizing for different contexts
 * - Domain-based logo selection (VRP, Fleet, Enterprise)
 * - Optional domain text display
 * - Click handler for domain switching
 * - Smooth hover animations
 */
export const DecoyLogo: React.FC<DecoyLogoProps> = ({
  size = 'medium',
  showDomainText = false,
  height,
  onClick,
  forceDomain,
}) => {
  const { currentDomain } = useDomainTheme();
  
  // Determine which domain to use
  const activeDomain = forceDomain || currentDomain;
  
  // Get logo source and domain info
  const getLogo = () => {
    switch (activeDomain) {
      case 'vrp':
        return {
          src: decoyLogoVrp,
          alt: 'DECOY VRP - Vehicle Routing Optimization',
          domainText: 'VRP Optimization',
        };
      case 'fleet':
        return {
          src: decoyLogoFleet,
          alt: 'DECOY Fleet - Fleet Management System',
          domainText: 'Fleet Management',
        };
      case 'enterprise':
        return {
          src: decoyLogoEnterprise,
          alt: 'DECOY Enterprises - Corporate Solutions',
          domainText: 'DECOY Enterprises',
        };
      default:
        return {
          src: decoyLogoFleet,
          alt: 'DECOY Logo',
          domainText: 'Fleet Management',
        };
    }
  };
  
  const logo = getLogo();
  
  return (
    <LogoContainer
      clickable={!!onClick}
      size={size}
      onClick={onClick}
      role={onClick ? 'button' : 'img'}
      aria-label={onClick ? `Switch domain - Currently ${logo.domainText}` : logo.alt}
    >
      <img
        src={logo.src}
        alt={logo.alt}
        style={{
          height: height ? `${height}px` : undefined,
          width: 'auto',
          objectFit: 'contain',
        }}
      />
      {showDomainText && (
        <DomainText>
          {logo.domainText}
        </DomainText>
      )}
    </LogoContainer>
  );
};

export default DecoyLogo;